import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Menu, LogOut, Clock, ChevronRight, ChevronLeft } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { usePageVisibility } from "@/lib/usePageVisibility";
import { NAV_CATALOG_FALLBACK } from "@/lib/navDefaults";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Stable test ids (kept identical to the previous hardcoded nav).
const NAV_TESTID = { "ronin-box": "nav-ronin", "in-person-training": "nav-inperson" };
const testidFor = (id) => NAV_TESTID[id] || `nav-${id}`;

// Renders a single nav entry. `variant` drives the row styling (primary vs secondary).
// Visibility + badges follow the same rules the hardcoded nav used.
function NavEntry({ item, variant, byKey }) {
  const entry = item.vkey ? byKey[item.vkey] : null;
  if (entry?.status === "hidden") return null;
  const comingSoon = entry?.status === "coming_soon";

  const cls =
    variant === "primary"
      ? "hover:text-[#FF331F] transition-colors inline-flex items-center gap-1.5"
      : "hover:text-white transition-colors inline-flex items-center gap-1.5";

  const badge =
    item.badge === "new" ? (
      <span className="text-[8px] tracking-[0.2em] bg-[#FF331F] text-white px-1 py-0.5 rounded-sm">NEW</span>
    ) : item.badge === "new_or_soon" ? (
      comingSoon ? (
        <span className="text-[8px] tracking-[0.2em] bg-amber-500 text-black px-1 py-0.5 rounded-sm">SOON</span>
      ) : (
        <span className="text-[8px] tracking-[0.2em] bg-[#FF331F] text-white px-1 py-0.5 rounded-sm">NEW</span>
      )
    ) : comingSoon ? (
      <Clock className="h-3 w-3 text-amber-400" />
    ) : null;

  const inner = (
    <>
      {item.label}
      {badge}
    </>
  );

  if (item.external) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testidFor(item.id)}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={item.href} className={cls} data-testid={testidFor(item.id)}>
      {inner}
    </Link>
  );
}

// Horizontally-scrollable nav row with a mobile "slide" hint (right-edge fade + pulsing
// chevron) that appears only when the row overflows and hides once scrolled to the end.
function ScrollNavRow({ trackClassName, children }) {
  const ref = useRef(null);
  const [showRight, setShowRight] = useState(false);
  const [showLeft, setShowLeft] = useState(false);

  const check = () => {
    const el = ref.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth > 4;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    const atStart = el.scrollLeft <= 4;
    setShowRight(overflow && !atEnd);
    setShowLeft(overflow && !atStart);
  };

  useEffect(() => {
    check();
    const t = setTimeout(check, 400); // re-check after fonts/layout settle
    window.addEventListener("resize", check);
    return () => { clearTimeout(t); window.removeEventListener("resize", check); };
  }, []);

  return (
    <div className="relative max-w-7xl mx-auto">
      <div ref={ref} onScroll={check} className="overflow-x-auto scrollbar-none">
        <div className={trackClassName}>{children}</div>
      </div>
      <div
        aria-hidden
        data-testid="nav-scroll-hint-left"
        className={`md:hidden pointer-events-none absolute inset-y-0 left-0 flex items-center pr-10 pl-2 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-transparent transition-opacity duration-300 ${showLeft ? "opacity-100" : "opacity-0"}`}
      >
        <ChevronLeft className="h-4 w-4 text-[#FF331F] animate-pulse" />
      </div>
      <div
        aria-hidden
        data-testid="nav-scroll-hint"
        className={`md:hidden pointer-events-none absolute inset-y-0 right-0 flex items-center pl-10 pr-2 bg-gradient-to-l from-[#050505] via-[#050505]/85 to-transparent transition-opacity duration-300 ${showRight ? "opacity-100" : "opacity-0"}`}
      >
        <ChevronRight className="h-4 w-4 text-[#FF331F] animate-pulse" />
      </div>
    </div>
  );
}

export function SiteHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { byKey } = usePageVisibility();

  const [navItems, setNavItems] = useState(NAV_CATALOG_FALLBACK);
  useEffect(() => {
    api.get("/nav").then(({ data }) => {
      if (Array.isArray(data?.items) && data.items.length) setNavItems(data.items);
    }).catch(() => {});
  }, []);

  const primary = navItems.filter((i) => i.row === "primary");
  const secondary = navItems.filter((i) => i.row === "secondary");

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#050505]/85 border-b border-[#27272A]" data-testid="site-header">
      {/* Row 1 — Logo (home) + utility buttons */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Better Through Boxing — Home" className="flex items-center group">
          <BrandMark size="lg" />
          <span className="hidden lg:block ml-3 pl-3 border-l border-zinc-700 text-[10px] uppercase tracking-[0.3em] text-zinc-400 leading-tight">
            Better<br/>Through<br/>Boxing
          </span>
        </Link>

        <div className="flex items-center gap-2 md:gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="user-menu-trigger">
                  <Menu className="h-4 w-4 md:hidden" />
                  <span className="hidden md:inline">{user.name?.split(" ")[0] || "Account"}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#0F0F0F] border-[#27272A] text-zinc-100" align="end">
                <DropdownMenuLabel className="text-zinc-400 text-xs uppercase tracking-widest">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#27272A]" />
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin-dash">Admin Dashboard</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="menu-client-portal">My Portal</DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-[#27272A]" />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} className="text-zinc-300 hover:text-white" data-testid="header-login-btn">Sign in</Button>
              <Button onClick={() => navigate("/book?package=virtual_40")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm text-xs md:text-sm px-3 md:px-4" data-testid="header-book-btn">Book session</Button>
            </>
          )}
        </div>
      </div>

      {/* Row 2 — Primary nav (what you DO with Coach) */}
      <nav
        className="border-t border-[#27272A] bg-[#050505]/60 backdrop-blur-xl"
        aria-label="Primary"
        data-testid="site-nav-row"
      >
        <ScrollNavRow trackClassName="w-max mx-auto px-6 md:px-10 h-11 flex items-center gap-5 md:gap-9 whitespace-nowrap text-[11px] md:text-xs uppercase tracking-[0.22em] md:tracking-[0.28em] font-bold text-zinc-300">
          {primary.map((item) => (
            <NavEntry key={item.id} item={item} variant="primary" byKey={byKey} />
          ))}
        </ScrollNavRow>
      </nav>

      {/* Row 3 — Secondary nav (About / content) */}
      <nav
        className="border-t border-[#27272A] bg-[#050505]/40"
        aria-label="Secondary"
        data-testid="site-nav-row-secondary"
      >
        <ScrollNavRow trackClassName="w-max mx-auto px-6 md:px-10 h-9 flex items-center gap-5 md:gap-8 whitespace-nowrap text-[10px] md:text-[11px] uppercase tracking-[0.22em] md:tracking-[0.28em] font-bold text-zinc-500">
          {secondary.map((item) => (
            <NavEntry key={item.id} item={item} variant="secondary" byKey={byKey} />
          ))}
        </ScrollNavRow>
      </nav>
    </header>
  );
}
